const QUANTITY_SELECTOR_UPDATE_EVENT = 'quantity-selector:update';

function isPdf(file) {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name || '');
}

function isImage(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|avif|svg)$/i.test(file.name || '');
}

function createPdfPreview(url, filename) {
  const objectEl = document.createElement('object');
  objectEl.type = 'application/pdf';
  objectEl.data = url;

  const fallback = document.createElement('p');
  fallback.style.margin = '0';
  fallback.style.padding = '16px';

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = filename ? `View ${filename}` : 'View PDF';

  fallback.appendChild(link);
  objectEl.appendChild(fallback);

  return objectEl;
}

function createImagePreview(url, filename) {
  const img = document.createElement('img');
  img.src = url;
  img.alt = filename ? `Preview: ${filename}` : 'Selected file preview';
  img.loading = 'lazy';
  return img;
}

function setupArtworkUpload(root) {
  const input = root.querySelector('.artwork-upload__input[type="file"]');
  const preview = root.querySelector('[data-artwork-upload-preview]');
  const previewMedia = root.querySelector('[data-artwork-upload-preview-media]');
  const clearButton = root.querySelector('[data-artwork-upload-clear]');
  const buttonLabel = root.querySelector('.artwork-upload__button');

  if (!input || !preview || !previewMedia || !clearButton || !buttonLabel) return;

  const statusEl =
    root.querySelector('[data-artwork-upload-status]') ||
    (() => {
      const fallback = document.createElement('p');
      fallback.className = 'artwork-upload__status';
      fallback.setAttribute('data-artwork-upload-status', '');
      fallback.setAttribute('aria-live', 'polite');
      buttonLabel.insertAdjacentElement('afterend', fallback);
      return fallback;
    })();

  const formId = input.getAttribute('form');
  const linkedForm = formId ? document.getElementById(formId) : null;
  const productFormComponent = linkedForm
    ? linkedForm.closest('product-form-component')
    : root.closest('product-form-component');

  const quantityInput =
    linkedForm?.querySelector('input[name="quantity"]') || productFormComponent?.querySelector('input[name="quantity"]');
  const enforcementMode = (root.dataset.artworkUploadEnforcement || '').toLowerCase();
  const fileKind = (root.dataset.artworkUploadFileKind || 'artwork').toLowerCase();
  const rawRequiredCount = parseInt(root.dataset.artworkUploadRequiredCount || '1', 10);
  const requiredCountPerUnit = Number.isNaN(rawRequiredCount) ? 1 : Math.max(rawRequiredCount, 1);

  let selectedFiles = [];
  let activeObjectUrls = [];
  let currentErrorType = null;

  const errorEl = document.createElement('p');
  errorEl.className = 'artwork-upload__error';
  errorEl.setAttribute('role', 'alert');
  errorEl.hidden = true;
  errorEl.textContent = 'Please upload your artwork before adding to cart.';
  statusEl.insertAdjacentElement('afterend', errorEl);

  function setError(message, type) {
    currentErrorType = type;
    errorEl.textContent = message;
    errorEl.hidden = false;
    statusEl.classList.add('artwork-upload__status--error');
    buttonLabel.classList.add('artwork-upload__button--error');
  }

  function clearError(type) {
    if (type && currentErrorType !== type) return;
    currentErrorType = null;
    errorEl.hidden = true;
    statusEl.classList.remove('artwork-upload__status--error');
    buttonLabel.classList.remove('artwork-upload__button--error');
  }

  function revokeObjectUrls() {
    for (const url of activeObjectUrls) {
      URL.revokeObjectURL(url);
    }
    activeObjectUrls = [];
  }

  function getMaxUploads() {
    const quantity = parseInt(quantityInput?.value || '1', 10);
    if (Number.isNaN(quantity)) return 1;
    const safeQuantity = Math.max(quantity, 1);
    if (enforcementMode === 'exact') return requiredCountPerUnit * safeQuantity;
    return safeQuantity;
  }

  function getKindLabel(count) {
    if (fileKind === 'pdf') return count === 1 ? 'PDF file' : 'PDF files';
    if (fileKind === 'image') return count === 1 ? 'image file' : 'image files';
    return count === 1 ? 'artwork file' : 'artwork files';
  }

  function getOverLimitMessage(fileCount, maxUploads) {
    const overflowCount = fileCount - maxUploads;
    return `You selected ${fileCount} files, but quantity is ${maxUploads}. Remove ${overflowCount} file${overflowCount === 1 ? '' : 's'} to continue.`;
  }

  function getExactCountMessage(fileCount, requiredTotal) {
    const diff = requiredTotal - fileCount;
    if (diff > 0) {
      return `Upload exactly ${requiredTotal} ${getKindLabel(requiredTotal)}. Add ${diff} more ${getKindLabel(diff)} to continue.`;
    }
    if (diff < 0) {
      const overflow = Math.abs(diff);
      return `Upload exactly ${requiredTotal} ${getKindLabel(requiredTotal)}. Remove ${overflow} ${getKindLabel(overflow)} to continue.`;
    }
    return '';
  }

  function syncInputWithFiles() {
    if (selectedFiles.length === 0) {
      input.value = '';
      return;
    }

    const dataTransfer = new DataTransfer();
    for (const file of selectedFiles) {
      dataTransfer.items.add(file);
    }
    input.files = dataTransfer.files;
  }

  function updateStatus() {
    const uploaded = selectedFiles.length;
    const requiredTotal = getMaxUploads();

    if (enforcementMode === 'exact') {
      const diff = requiredTotal - uploaded;
      if (diff > 0) {
        statusEl.textContent = `Uploaded ${uploaded} of ${requiredTotal} required. Upload ${diff} more ${getKindLabel(diff)}.`;
        return;
      }
      if (diff < 0) {
        const overflow = Math.abs(diff);
        statusEl.textContent = `Uploaded ${uploaded} of ${requiredTotal} required. Remove ${overflow} ${getKindLabel(overflow)}.`;
        return;
      }
      statusEl.textContent = `Uploaded ${uploaded} of ${requiredTotal} required.`;
      return;
    }

    const remaining = Math.max(requiredTotal - uploaded, 0);
    statusEl.textContent = `Uploaded ${uploaded} of ${requiredTotal}. You can upload ${remaining} more file${remaining === 1 ? '' : 's'}.`;
  }

  function validateFileCount() {
    const requiredTotal = getMaxUploads();
    const selectedCount = selectedFiles.length;

    if (enforcementMode === 'exact') {
      if (selectedCount !== requiredTotal) {
        statusEl.classList.add('artwork-upload__status--error');
        buttonLabel.classList.add('artwork-upload__button--error');
        errorEl.hidden = true;
        return false;
      }
      statusEl.classList.remove('artwork-upload__status--error');
      buttonLabel.classList.remove('artwork-upload__button--error');
      errorEl.hidden = true;
      return true;
    }

    if (selectedCount > requiredTotal) {
      setError(getOverLimitMessage(selectedCount, requiredTotal), 'limit');
      return false;
    }

    clearError('limit');
    return true;
  }

  function createPreviewItem(file, index) {
    const item = document.createElement('div');
    item.className = 'artwork-upload__preview-item';

    const media = document.createElement('div');
    media.className = 'artwork-upload__preview-file';

    const objectUrl = URL.createObjectURL(file);
    activeObjectUrls.push(objectUrl);

    let previewEl = null;
    if (isImage(file)) previewEl = createImagePreview(objectUrl, file.name);
    else if (isPdf(file)) previewEl = createPdfPreview(objectUrl, file.name);
    else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = file.name || `File ${index + 1}`;
      previewEl = link;
    }

    media.appendChild(previewEl);

    const meta = document.createElement('div');
    meta.className = 'artwork-upload__preview-meta';

    const fileName = document.createElement('p');
    fileName.className = 'artwork-upload__filename';
    fileName.textContent = file.name || `File ${index + 1}`;

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'artwork-upload__remove';
    removeButton.textContent = 'Remove';
    removeButton.setAttribute('aria-label', `Remove ${file.name || `file ${index + 1}`}`);
    removeButton.addEventListener('click', () => {
      selectedFiles = selectedFiles.filter((_, fileIndex) => fileIndex !== index);
      syncInputWithFiles();
      refresh();
    });

    meta.append(fileName, removeButton);
    item.append(media, meta);

    return item;
  }

  function renderPreview() {
    previewMedia.replaceChildren();
    revokeObjectUrls();

    if (selectedFiles.length === 0) {
      preview.hidden = true;
      return;
    }

    selectedFiles.forEach((file, index) => {
      previewMedia.appendChild(createPreviewItem(file, index));
    });
    preview.hidden = false;
  }

  function refreshButtonLabel() {
    buttonLabel.textContent = selectedFiles.length > 0 ? 'Change files' : 'Choose files';
  }

  function refresh() {
    renderPreview();
    refreshButtonLabel();
    updateStatus();
    validateFileCount();
  }

  function clearSelection() {
    selectedFiles = [];
    syncInputWithFiles();
    refresh();
  }

  function onChange() {
    selectedFiles = Array.from(input.files || []);
    clearError();
    refresh();
  }

  function blockIfInvalid(event) {
    const maxUploads = getMaxUploads();
    const fileCount = selectedFiles.length;

    if (enforcementMode === 'exact') {
      if (fileCount !== maxUploads) {
        event.preventDefault();
        event.stopImmediatePropagation();
        statusEl.classList.add('artwork-upload__status--error');
        buttonLabel.classList.add('artwork-upload__button--error');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      statusEl.classList.remove('artwork-upload__status--error');
      buttonLabel.classList.remove('artwork-upload__button--error');
      clearError();
      return;
    }

    if (fileCount === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setError('Please upload your artwork before adding to cart.', 'required');
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (fileCount > maxUploads) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setError(getOverLimitMessage(fileCount, maxUploads), 'limit');
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    clearError();
  }

  function onQuantityUpdate(event) {
    if (!(event.target instanceof Node)) {
      refresh();
      return;
    }

    if (linkedForm && !linkedForm.contains(event.target)) return;
    if (!linkedForm && productFormComponent && !productFormComponent.contains(event.target)) return;

    refresh();
  }

  if (productFormComponent) {
    const atcButton = productFormComponent.querySelector('[ref="addToCartButton"]');
    if (atcButton) {
      atcButton.addEventListener('click', blockIfInvalid, true);
    }

    const form = productFormComponent.querySelector('form');
    if (form) {
      form.addEventListener('submit', blockIfInvalid, true);
    }
  }

  input.addEventListener('invalid', (event) => {
    event.preventDefault();
    blockIfInvalid(event);
  });

  input.addEventListener('change', onChange);
  clearButton.addEventListener('click', (event) => {
    event.preventDefault();
    clearSelection();
  });

  quantityInput?.addEventListener('input', refresh);
  quantityInput?.addEventListener('change', refresh);
  document.addEventListener(QUANTITY_SELECTOR_UPDATE_EVENT, onQuantityUpdate);

  refresh();
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-artwork-upload]').forEach(setupArtworkUpload);
});
